import React, { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import usePermissions from "hooks/usePermissions";
import useCollectionMutation from "collections/hooks/useCollectionMutation";
import { ButtonToolbar, MenuItem, DeleteMenuItem } from "components/bootstrap";
import { ConfirmDialog, IfPermitted, ShareButton } from "components/common";
import { MoreActions } from "components/common/EntityDataTable";
import EntityShareModal from "components/permissions/EntityShareModal";
import useSelectedEntities from "components/common/EntityDataTable/hooks/useSelectedEntities";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
import { useTranslation } from "react-i18next";
import CollectionsRoutes from "../../Routes";
const CollectionActions = ({ id, title }) => {
  const { t } = useTranslation("enterprise_collections");
  const { isAnyPermitted } = usePermissions();
  const queryClient = useQueryClient();
  const refetchCollections = useCallback(
    () => queryClient.invalidateQueries({
      queryKey: [COLLECTIONS_QUERY_KEY]
    }),
    [queryClient]
  );
  const { deselectEntity } = useSelectedEntities();
  const { deleteCollection } = useCollectionMutation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCollectionShareModal, setShowCollectionShareModal] = useState(false);
  const [showCollectionContentShareModal, setShowCollectionContentShareModal] = useState(false);
  const toggleCollectionShareModal = () => setShowCollectionShareModal(!showCollectionShareModal);
  const toggleCollectionContentShareModal = () => setShowCollectionContentShareModal(!showCollectionContentShareModal);
  const handleShareClose = (type) => {
    refetchCollections();
    if (type === "collection") {
      toggleCollectionShareModal();
    } else if (type === "collectionContent") {
      toggleCollectionContentShareModal();
    }
  };
  const cancelDelete = () => {
    setShowDeleteDialog(false);
    deselectEntity(id);
  };
  const handleDelete = () => {
    deleteCollection({ id }).then(() => {
      deselectEntity(id);
      setShowDeleteDialog(false);
    });
  };
  const onDelete = () => {
    setShowDeleteDialog(true);
  };
  const enableDropdownMenu = isAnyPermitted([
    `collection_entities:edit:${id}`,
    `collections:edit:${id}`,
    `collections:delete:${id}`
  ]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, showDeleteDialog && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showDeleteDialog,
      title: t("collection_actions.delete_title"),
      onCancel: cancelDelete,
      onConfirm: handleDelete
    },
    /* @__PURE__ */ React.createElement("p", null, t("collection_actions.delete_confirm", { title }))
  ), showCollectionContentShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: id,
      entityType: "collection_entities",
      entityTypeTitle: t("shared.collection_content"),
      entityTitle: title,
      description: t("collection_actions.share_content_description"),
      onClose: () => handleShareClose("collectionContent")
    }
  ), showCollectionShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: id,
      entityType: "collection",
      entityTypeTitle: t("shared.collection"),
      entityTitle: title,
      description: t("shared.share_collection_description"),
      onClose: () => handleShareClose("collection")
    }
  ), /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(
    ShareButton,
    {
      title: t("shared.share_content"),
      entityId: id,
      entityType: "collection_entities",
      onClick: toggleCollectionContentShareModal,
      bsSize: "xs",
      bsStyle: "primary",
      showTitle: true
    }
  ), /* @__PURE__ */ React.createElement(
    ShareButton,
    {
      entityId: id,
      entityType: "collection",
      onClick: toggleCollectionShareModal,
      bsSize: "xs",
      title: t("shared.manage"),
      showTitle: true
    }
  ), /* @__PURE__ */ React.createElement(MoreActions, { disabled: !enableDropdownMenu }, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`collection_entities:edit:${id}`, `collection:edit:${id}`], anyPermissions: true }, /* @__PURE__ */ React.createElement(MenuItem, { href: CollectionsRoutes.show(id) }, t("shared.edit"))), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `collections:delete:${id}` }, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: onDelete }, t("shared.delete"))))));
};
export default CollectionActions;
